import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { UsersService } from 'src/app/shared/services/users.service';
import { InactivityService } from 'src/app/shared/services/inactivity.service';
import { SessionService } from 'src/app/shared/services/session.service';
import Swal from 'sweetalert2';

@Injectable()
export class GlobalHttpInterceptorService implements HttpInterceptor {
	private isCompany: boolean = false;
	private registerFormsKeysAndLabelsList: any[] = [];

	constructor(
		private localStorage: LocalStorageService,
		private router: Router,
		private userService: UsersService,
		private inactivityService: InactivityService,
		private sessionService: SessionService
	) {}

	private convertKeysToLabels(key: string): string {
		this.registerFormsKeysAndLabelsList = [
			{ Birthdate: 'Fecha de nacimiento' },
			{ Nationality: 'Nacionalidad' },
			{ Profession: 'Selecciona tu ocupación' },
			{ Cellphone: this.isCompany ? 'Celular de la empresa' : 'Celular' },
			{ Rne: 'RNE' },
			{ CompanyType: 'Tipo de empresa' },
			{ CompanyPhone: 'Teléfono fijo de la empresa' },
			{ FinancialContactName: 'Nombre del contacto financiero' },
			{ FinancialContactEmail: 'Correo del contacto financiero' },
			{ FinancialContactEmailConfirmation: 'Confirmar correo - contacto financiero' },
			{ Cnh: 'Número da CNH' },
			{ CnhFirstLicenseDate: 'Data da primeira habilitação' },
			{ CnhExpirationDate: 'Vigência da CNH' },
			{ CnhCategory: 'Categoria' },
			{ Cep: this.isCompany ? 'CEP da empresa' : 'CEP' },
			{ Street: this.isCompany ? 'Dirección de la empresa' : 'Dirección' },
			{ Number: 'Número' },
			{ Neighborhood: 'Barrio' },
			{ City: 'Ciudad' },
			{ State: 'Departamento' },
			{ Name: this.isCompany ? 'Razón Social' : 'Nombre Completo' },
			{ Document: this.isCompany ? 'NIT' : 'Documento de identificación' },
			{ Email: this.isCompany ? 'Correo de la empresa' : 'Correo electrónico' },
			{ EmailConfirmation: this.isCompany ? 'Confirmar correo' : 'Confirmar su correo' },
			{ Password: 'Contraseña' },
			{ PasswordConfirmation: 'Confirmar contraseña' },
			{ HasAcceptedPrivacyPolicy: 'Termo de aceite' },
			{ Phone: 'Teléfono fijo' },
			{ ExpiryMonth: 'Fecha de vencimiento de tarjeta' },
		];

		const foundItem = this.registerFormsKeysAndLabelsList.find(item => item.hasOwnProperty(key));
		if (!foundItem) return '';
		return foundItem[Object.keys(foundItem)[0]];
	}

	intercept(
		request: HttpRequest<any>,
		next: HttpHandler
	): Observable<HttpEvent<any>> {
		this.isCompany = this.router.url.includes('empresa');

		const PUBLIC_ROUTES = [
			'/conta/login',
			'/api/v1/authentications/login',
		];

		const isPublicRequest = PUBLIC_ROUTES.some(r => request.url.includes(r));
		const isLoginRequest = request.method === 'POST' && /\/authentications(?:\?|$)/.test(request.url);
		const isRefreshRequest = request.url.includes('/refresh-token');

		const userStr = this.localStorage.getItem('ROD-logged.user');
		let user: any = null;

		if (userStr) {
			try {
				user = JSON.parse(userStr);
			} catch {}
		}

		/* =====================================================
		🔐 AGREGAR AUTH HEADER
		====================================================== */
		if (
			user?.token &&
			!isPublicRequest &&
			!request.url.includes('google') &&
			!request.url.includes('brazilsouth')
		) {
			request = request.clone({
				setHeaders: {
					Authorization: `Bearer ${user.token}`
				}
			});
		}

		/* =====================================================
		SESSION ID
		====================================================== */
		if (request.url.includes('api/v1')) {
			let sessionId = this.localStorage.getItem('RoD-SessionId');
			if (!sessionId) {
				sessionId = this.userService.newGuid();
				this.localStorage.setItem('RoD-SessionId', sessionId);
			}
			request = request.clone({
				setHeaders: { sessionId }
			});
		}

		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				const status = error.status;
				const url = request.url;
				const backend = error?.error || {};
				const message = backend.message || '';
				const traceId = backend.traceId
					? `<span class="trace-id">${backend.traceId}</span>`
					: '';
				const isInactiveAccountLoginError = isLoginRequest && message === 'ACCOUNT_INACTIVE_60_DAYS';

				const isAzure = url.includes('brazilsouth.logic.azure.com');

				/* ============================================
				401 – LOGIN ERRADO (sin popup, componente lo maneja)
				============================================ */
				if (status === 401 && isLoginRequest) {
					return throwError(error);
				}

				/* ============================================
				401 – REFRESH TOKEN FALLÓ
				// FIX: InactivityService ya muestra el popup cuando el
				// refresh falla. El interceptor solo hace el logout hard,
				// SIN mostrar un segundo Swal, evitando mensajes duplicados.
				============================================ */
				if (status === 401 && isRefreshRequest) {
					console.error('[SESSION BREAK - REFRESH]');
					this.forceLogoutHard('refresh_failed');
					return throwError(error);
				}

				/* ============================================
				401 – SESIÓN EXPIRADA (GENERAL)
				// FIX: se espera el .then() antes de hacer logout
				// para que el usuario alcance a leer el mensaje.
				============================================ */
				if (status === 401 && !isPublicRequest && !isAzure) {
					void Swal.fire({
						title: 'Sesión expirada',
						html: `<div>${message || 'Tu sesión expiró. Inicia sesión nuevamente.'}</div>`,
						icon: 'warning',
						confirmButtonText: 'Ok',
						buttonsStyling: false,
						customClass: { confirmButton: 'btn btn-primary' },
					}).then(() => {
						console.error('[SESSION BREAK]');
						this.forceLogoutHard('session_expired');
					}).catch((e) => {
						console.error('Error mostrando el Swal:', e);
						this.forceLogoutHard('session_expired');
					});

					return throwError(error);
				}

				/* ============================================
				404 – BASKETS
				============================================ */
				if (status === 404 && url.includes('baskets')) {
					void Swal.fire({
						title: '¡Error!',
						html: `<div>${message}</div>${traceId}`,
						icon: 'error',
						confirmButtonText: 'Ok',
						buttonsStyling: false,
						customClass: { confirmButton: 'btn btn-primary' },
					}).then(() => {
						this.localStorage.removeItem('ROD-simulation');
						this.router.navigate(['/simulador/elegir-renta-de-carro']);
					}).catch((e) => {
						console.error('Error mostrando el Swal:', e);
						this.localStorage.removeItem('ROD-simulation');
						this.router.navigate(['/simulador/elegir-renta-de-carro']);
					});

					return throwError(error);
				}

				/* ============================================
				403 – AUTHENTICATIONS
				============================================ */
				if (status === 403 && url.includes('authentications')) {
					if (isInactiveAccountLoginError) {
					// FIX: No redirigir cuando la cuenta está inactiva
					// El login.component.ts maneja el mensaje de inactividad
					return throwError(error);
				}

				// Solo redireccionar si NO es error de inactividad
				if (message && !message.includes('INACTIVE')) {
					void Swal.fire({
							customClass: { confirmButton: 'btn btn-primary' },
						}).then(() => {
							this.localStorage.setItem('ROD-email.verify', request.body?.email);
							this.router.navigate(['/renta-de-carros-mensual/rapido/finalizar']);
						}).catch((e) => {
							console.error('Error mostrando el Swal:', e);
						});
					}
					return throwError(error);
				}

				/* ============================================
				400 – BASKETS (VALIDACIONES)
				============================================ */
				if (status === 400 && url.includes('baskets')) {
					if (backend.notifications?.length) {
						const errorList = backend.notifications
							.map((n: string) => `<li>${n}</li>`)
							.join('');

						void Swal.fire({
							title: 'ALERTA EN LOS CAMPOS:',
							html: `<ul class="p-0">${errorList}</ul>${traceId}`,
							icon: 'warning',
							confirmButtonText: 'Ok',
							showCloseButton: true,
							buttonsStyling: false,
							customClass: { confirmButton: 'btn btn-primary' },
						});
					} else {
						void Swal.fire({
							title: '¡Alerta!',
							html: `<div>${message}</div>${traceId}`,
							icon: 'warning',
							confirmButtonText: 'Ok',
							buttonsStyling: false,
							customClass: { confirmButton: 'btn btn-primary' },
						});
					}

					return throwError(error);
				}

				/* ============================================
				ERRORES CON FIELDS
				============================================ */
				if (backend.fields?.length) {
					const errorList = backend.fields
						.map((f: any) => {
							const label = this.convertKeysToLabels(f.key || '');
							return label
								? `<li><strong>${label}:</strong> ${f.message}</li>`
								: `<li>${f.message}</li>`;
						})
						.join('');

					void Swal.fire({
						title: 'Error en los siguientes campos:',
						html: `<ul class="p-0">${errorList}</ul>${traceId}`,
						icon: 'warning',
						confirmButtonText: 'Ok',
						buttonsStyling: false,
						customClass: { confirmButton: 'btn btn-primary' },
					});

					return throwError(error);
				}

				/* ============================================
				MENSAJE GENÉRICO
				============================================ */
				if (message) {
					void Swal.fire({
						title: '¡Alerta!',
						html: `<div>${message}</div>${traceId}`,
						icon: 'warning',
						confirmButtonText: 'Ok',
						buttonsStyling: false,
						customClass: { confirmButton: 'btn btn-primary' },
					});
				}

				return throwError(error);
			})
		);
	}

	/* =====================================================
	HARD LOGOUT ÚNICO
	====================================================== */
	private forceLogoutHard(reason: 'session_expired' | 'refresh_failed' = 'session_expired'): void {
		this.inactivityService.stopWatching();
		this.sessionService.clearSessionHard(reason);
	}
}